import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Priority, User } from "@/types";
import { Lock } from "lucide-react";
import { useMemo, useRef } from "react";
import type { FieldArrayWithId } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { AssignTeamCard } from "./AssignTeamCard";
import {
    ASSIGNEE_STATUS_OPTIONS,
    cardBodyClass,
    cardClass,
    cardHeaderClass,
    AssigneeLoad,
    DisplayUser,
    fieldLabelClass,
    PRIORITY_TYPE_LABEL,
} from "./priority-form-constants";
import { PriorityFormSidebar } from "./PriorityFormSidebar";
import { PppMultiSelect } from "@/components/priorities/PppMultiSelect";
import { PriorityReadOnlyNotice } from "./PriorityReadOnlyNotice";
import type { PriorityFormValues } from "./priority-form-schema";

function toDisplayUser(u: { pid: string; fullName: string; avatarUrl?: string; position?: string }): DisplayUser {
    return { pid: u.pid, fullName: u.fullName, avatarUrl: u.avatarUrl, position: u.position };
}

export interface PriorityFormCreateViewProps {
    mode?: "create" | "edit";
    priorityCode?: string;
    initialData?: Partial<Priority>;
    assignmentFields: FieldArrayWithId<PriorityFormValues, "assignments", "id">[];
    users: User[];
    isLoadingUsers: boolean;
    userSearch: string;
    setUserSearch: (s: string) => void;
    onAssigneePidsChange: (pids: string[]) => void;
    onSetDri: (driPid: string) => void;
    loadByPid?: Map<string, AssigneeLoad>;
    /** True when the current user may not change task-level fields: details, status, assignees, DRI. */
    readOnly?: boolean;
    /** True when the current user has their own assignment row, which stays editable even when readOnly. */
    canEditOwnRow?: boolean;
    /** DRI's name, so the banner can say who to ask. */
    driName?: string;
    /** True when the task has no DRI at all, in which case there is nobody to ask. */
    isDriMissing?: boolean;
}

export function PriorityFormCreateView({
    mode = "create",
    priorityCode,
    initialData,
    assignmentFields,
    users,
    isLoadingUsers,
    userSearch,
    setUserSearch,
    onAssigneePidsChange,
    onSetDri,
    loadByPid,
    readOnly = false,
    canEditOwnRow = false,
    driName,
    isDriMissing = false,
}: PriorityFormCreateViewProps) {
    const form = useFormContext<PriorityFormValues>();
    const { user: currentUser } = useAuth();
    const isEdit = mode === "edit";

    // Persists resolved DisplayUser objects when the `users` prop narrows (e.g. search filtering),
    // so assignment rows retain display names even if the user no longer appears in the prop.
    const userCacheRef = useRef<Map<string, DisplayUser>>(new Map());

    const userByPid = useMemo(() => {
        const m = new Map<string, DisplayUser>();
        // The creator is auto-assigned as DRI; the paginated user search may not include them.
        if (currentUser) {
            const me = toDisplayUser(currentUser);
            m.set(currentUser.pid, me);
            userCacheRef.current.set(currentUser.pid, me);
        }
        users.forEach((u) => {
            const displayUser = toDisplayUser(u);
            m.set(u.pid, displayUser);
            userCacheRef.current.set(u.pid, displayUser);
        });
        initialData?.assignments?.forEach((a) => {
            if (!m.has(a.assignedUser.pid)) {
                const displayUser = toDisplayUser(a.assignedUser);
                m.set(a.assignedUser.pid, displayUser);
                userCacheRef.current.set(a.assignedUser.pid, displayUser);
            }
        });
        // Fall back to cache for users that were present in a previous render
        // but have since been filtered out of the `users` prop.
        assignmentFields.forEach((field) => {
            const cached = userCacheRef.current.get(field.userPid);
            if (cached && !m.has(field.userPid)) {
                m.set(field.userPid, cached);
            }
        });
        return m;
    }, [users, initialData, assignmentFields, currentUser]);

    const assigneeNameByPid = useMemo(() => {
        const m = new Map<string, string>();
        userByPid.forEach((u, pid) => m.set(pid, u.fullName));
        return m;
    }, [userByPid]);

    return (
        <div className="m-0 min-w-0 text-sm font-sans leading-5 antialiased">
            {readOnly ? (
                <PriorityReadOnlyNotice driName={driName} isDriMissing={isDriMissing} canEditOwnRow={canEditOwnRow} />
            ) : null}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_390px] lg:items-start">
                <div className="flex min-w-0 flex-col gap-4">
                    {/* A `fieldset[disabled]` rather than a `disabled` prop threaded through every field: it
                        disables every descendant control natively, so a field added to this card later cannot
                        forget to opt in. Only task-level fields live here — the assignment rows are permissioned
                        per row inside AssignTeamCard, because an assignee still owns their own row.
                        The Radix widgets (Select, Popover, Switch) additionally carry an explicit `disabled`:
                        they open on pointerdown, which browsers still deliver inside a disabled fieldset, so
                        the fieldset alone leaves their dropdowns clickable. */}
                    <fieldset disabled={readOnly} className={cn("m-0 min-w-0 border-0 p-0", cardClass)}>
                        <div className={cardHeaderClass}>
                            <span className="text-sm font-medium">Priority details</span>
                        </div>
                        <div className={cardBodyClass}>
                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem className="mb-3.5">
                                        <FormLabel className={fieldLabelClass}>Priority title</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="What needs to get done this week?"
                                                className="h-9"
                                                autoFocus
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className={cn("mb-3.5 grid grid-cols-1 gap-3", isEdit ? "sm:grid-cols-2" : "sm:grid-cols-1")}>
                                <FormField
                                    control={form.control}
                                    name="type"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className={fieldLabelClass}>Priority type</FormLabel>
                                            <Select onValueChange={field.onChange} value={field.value} disabled={readOnly}>
                                                <FormControl>
                                                    <SelectTrigger className="h-9 text-sm font-normal [&>svg]:text-muted-foreground">
                                                        <SelectValue placeholder="Select type" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    <SelectItem value="SHP">{PRIORITY_TYPE_LABEL.SHP}</SelectItem>
                                                    <SelectItem value="WKR">{PRIORITY_TYPE_LABEL.WKR}</SelectItem>
                                                    <SelectItem value="DAS">{PRIORITY_TYPE_LABEL.DAS}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                {isEdit ? (
                                    <FormField
                                        control={form.control}
                                        name="status"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className={fieldLabelClass}>Priority status</FormLabel>
                                                <Select onValueChange={field.onChange} value={field.value ?? "queue"} disabled={readOnly}>
                                                    <FormControl>
                                                        <SelectTrigger className="h-9 text-sm font-normal">
                                                            <SelectValue placeholder="Select status" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {ASSIGNEE_STATUS_OPTIONS.map((opt) => (
                                                            <SelectItem key={opt.value} value={opt.value} title={opt.description}>
                                                                {opt.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                ) : null}
                            </div>

                            <FormField
                                control={form.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem className="mb-3.5">
                                        <FormLabel className={cn(fieldLabelClass, "flex items-center gap-1.5")}>
                                            Description
                                            <span className="text-xs font-normal text-muted-foreground">optional</span>
                                        </FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="More context for the team..."
                                                className={cn(
                                                    "resize-y text-sm",
                                                    isEdit ? "min-h-[120px]" : "min-h-[72px] resize-none"
                                                )}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="isConfidential"
                                render={({ field }) => (
                                    <FormItem>
                                        <div className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5">
                                            <div className="flex items-center gap-2">
                                                <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                                <div>
                                                    <FormLabel className="cursor-pointer text-sm font-medium leading-none">
                                                        Confidential
                                                    </FormLabel>
                                                    <p className="mt-0.5 text-xs text-muted-foreground">
                                                        Visible to leaders, the creator and assignees only.
                                                    </p>
                                                </div>
                                            </div>
                                            <FormControl>
                                                <Switch
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                    disabled={readOnly}
                                                />
                                            </FormControl>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="pppPids"
                                render={({ field }) => (
                                    <FormItem className="mt-3.5">
                                        <FormLabel className={cn(fieldLabelClass, "flex items-center gap-1.5")}>
                                            PPP
                                            <span className="text-xs font-normal text-muted-foreground">required</span>
                                        </FormLabel>
                                        <PppMultiSelect value={field.value} onChange={field.onChange} disabled={readOnly} />
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </fieldset>

                    <AssignTeamCard
                        isEdit={isEdit}
                        assignmentFields={assignmentFields}
                        users={users}
                        isLoadingUsers={isLoadingUsers}
                        userSearch={userSearch}
                        setUserSearch={setUserSearch}
                        onAssigneePidsChange={onAssigneePidsChange}
                        onSetDri={onSetDri}
                        initialData={initialData}
                        assigneeNameByPid={assigneeNameByPid}
                        userByPid={userByPid}
                        loadByPid={loadByPid}
                        readOnly={readOnly}
                    />
                </div>

                <PriorityFormSidebar
                    isEdit={isEdit}
                    priorityCode={priorityCode}
                    initialData={initialData}
                    assignmentFields={assignmentFields}
                    assigneeNameByPid={assigneeNameByPid}
                    userByPid={userByPid}
                />
            </div>
        </div>
    );
}

